import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  notification,
  Divider,
  Row,
  Col,
  AutoComplete,
} from "antd";
import ProjectSettingToggle from "./ProjectSettingToggle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  createPunchListItem,
  fetchNextPunchListItemNumber,
} from "../services/punchListsAPI";
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
} from "../services/templatesAPI";
import TemplateModal from "./TemplateModal";
import AttachmentsSection from "./AttachmentsSection";
import ProjectUserSelect from "./ProjectUserSelect";
import {
  PlusOutlined,
  EditOutlined,
  FileTextOutlined,
} from "@ant-design/icons";

const CreatePunchListItemModal = ({
  visible,
  onClose,
  punchListId,
  onCreated,
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const emailNotifications = Form.useWatch("email_notifications", form) ?? true;

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const initialValues = {
    status: "Incomplete",
    email_notifications: true,
  };

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ["templates", projectId, "punch_list_item"],
    queryFn: () => fetchTemplates(projectId, "punch_list_item"),
    enabled: !!projectId,
  });

  useEffect(() => {
    if (visible && punchListId) {
      fetchNextPunchListItemNumber(punchListId).then((data) => {
        form.setFieldsValue({
          number: data.next_number,
          email_notifications: true,
        });
      });
    }
  }, [visible, punchListId, form]);

  useEffect(() => {
    setSelectedTemplate(null);
  }, [projectId]);

  useEffect(() => {
    if (!visible) {
      setSelectedTemplate(null);
    }
  }, [visible]);

  const mutation = useMutation({
    mutationFn: createPunchListItem,
    onSuccess: (createdItem) => {
      queryClient.invalidateQueries(["punch-list-items"]);
      queryClient.invalidateQueries({
        queryKey: ["punch-list-items-drawing-link", punchListId],
      });
      onCreated?.(createdItem);
      onClose();
      form.resetFields();
      setSelectedTemplate(null);
      notification.success({ message: "Punch list item created successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to create punch list item",
        description: error.message,
      });
    },
  });

  const applyTemplateTitle = (template) => {
    form.setFieldsValue({
      title: template.item_title || "",
    });
  };

  const handleTemplateChange = async (templateId) => {
    if (templateId) {
      const template = templates?.templates?.find((t) => t._id === templateId);
      setSelectedTemplate(template);

      const formValues = form.getFieldsValue();
      const hasExistingData = !!(
        (formValues.title && String(formValues.title).trim() !== "") ||
        formValues.approver_id ||
        (formValues.assignee_ids && formValues.assignee_ids.length > 0) ||
        (formValues.location && String(formValues.location).trim() !== "") ||
        (formValues.attachments && formValues.attachments.length > 0)
      );

      if (hasExistingData) {
        Modal.confirm({
          title: "Are you sure?",
          content:
            "There is existing data in the form. Replace the title with the template default?",
          okText: "Yes",
          cancelText: "No",
          onOk: () => applyTemplateTitle(template),
          onCancel: () => {
            setSelectedTemplate(null);
          },
        });
      } else {
        applyTemplateTitle(template);
      }
    } else {
      setSelectedTemplate(null);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsTemplateModalVisible(true);
  };

  const handleEditTemplate = () => {
    if (selectedTemplate) {
      setEditingTemplate(selectedTemplate);
      setIsTemplateModalVisible(true);
    }
  };

  const handleTemplateModalClose = () => {
    setIsTemplateModalVisible(false);
    setEditingTemplate(null);
  };

  const handleTemplateDeleted = () => {
    setSelectedTemplate(null);
  };

  const handleTemplateSave = async (templateData) => {
    try {
      const basePayload = {
        name: templateData.name,
        item_title: templateData.item_title,
        project_id: projectId,
        type: "punch_list_item",
        days_duration: 7,
        distribution_list: [],
        steps: [],
        assignee_ids: [],
      };

      if (editingTemplate) {
        const updatedTemplate = await updateTemplate(editingTemplate._id, {
          name: templateData.name,
          item_title: templateData.item_title,
        });
        notification.success({ message: "Template updated successfully" });
        setSelectedTemplate(updatedTemplate);
        applyTemplateTitle(updatedTemplate);
      } else {
        await createTemplate(basePayload);
        notification.success({ message: "Template created successfully" });
      }

      queryClient.invalidateQueries({
        queryKey: ["templates", projectId, "punch_list_item"],
      });
      handleTemplateModalClose();
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail?.message ||
        error?.message ||
        "Failed to save template";
      notification.error({
        message: "Failed to save template",
        description: errorMessage,
      });
    }
  };

  const handleCreatePunchListItem = async (values) => {
    const punchListData = {
      ...values,
      project_id: projectId,
      attachments: values.attachments?.map((file) => file) || [],
    };
    const emailNotif = form.getFieldValue("email_notifications") ?? true;
    mutation.mutate({
      punchListId: punchListId,
      punchListItem: punchListData,
      emailNotifications: emailNotif,
    });
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedTemplate(null);
    onClose();
  };

  return (
    <Modal
      title="Create Punch List Item"
      open={visible}
      onCancel={handleClose}
      footer={null}
      className="w-full max-w-4xl"
    >
      <div className="mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
            <FileTextOutlined className="text-xs" />
            Template:
          </span>
          <Select
            placeholder="Select template..."
            value={selectedTemplate?._id}
            onChange={handleTemplateChange}
            allowClear
            loading={templatesLoading}
            options={templates?.templates?.map((template) => ({
              value: template._id,
              label: template.name,
            }))}
            className="flex-1"
            size="small"
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={handleCreateTemplate}
            size="small"
            className="p-1 h-6 w-6 text-gray-500 hover:text-blue-500"
            title="Add template"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={handleEditTemplate}
            disabled={!selectedTemplate}
            size="small"
            className="p-1 h-6 w-6 text-gray-500 hover:text-blue-500"
            title="Edit template"
          />
        </div>
      </div>

      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleCreatePunchListItem}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Number"
              name="number"
              rules={[{ required: true, message: "Please input the number!" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Status" name="status">
              <Select defaultValue="Incomplete">
                <Select.Option value="Complete">Complete</Select.Option>
                <Select.Option value="Incomplete">Incomplete</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Please input the title!" }]}
          className="w-full"
        >
          <AutoComplete
            options={templates?.templates?.map((t) => ({
              value: t.item_title || t.name,
            }))}
            filterOption={(inputValue, option) =>
              option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
          >
            <Input />
          </AutoComplete>
        </Form.Item>
        <Form.Item
          label="Approver"
          name="approver_id"
          rules={[{ required: true, message: "Please select the approver!" }]}
          className="w-full"
        >
          <ProjectUserSelect
            mode={null}
            placeholder="Select approver"
            className="w-full"
          />
        </Form.Item>
        <Form.Item
          label="Assignees"
          name="assignee_ids"
          rules={[{ required: true, message: "Please select the assignees!" }]}
          className="w-full"
        >
          <ProjectUserSelect
            placeholder="Select assignees"
            className="w-full"
          />
        </Form.Item>
        <Form.Item label="Location" name="location" className="w-full">
          <Input />
        </Form.Item>

        <Divider />

        <Form.Item name="attachments" label="Attachments" className="w-full">
          <AttachmentsSection projectId={projectId} />
        </Form.Item>

        <div className="project-settings-grid">
          <ProjectSettingToggle name="email_notifications" title="Email Notifications" initialValue={true} />
        </div>

        <Form.Item className="w-full">
          <Button
            type="primary"
            htmlType="submit"
            loading={mutation.isPending}
            className="w-full mt-4"
          >
            {emailNotifications
              ? "Create and Send Emails"
              : "Create Punch List Item"}
          </Button>
        </Form.Item>
      </Form>

      <TemplateModal
        visible={isTemplateModalVisible}
        onClose={handleTemplateModalClose}
        onSave={handleTemplateSave}
        editingTemplate={editingTemplate}
        templateType="punch_list_item"
        onTemplateDeleted={handleTemplateDeleted}
      />
    </Modal>
  );
};

export default CreatePunchListItemModal;
